// src/lib/omekaClient.js
import axios from "axios";

/** ⚙️ CONFIG & ENV */
const isVite = typeof import.meta !== "undefined" && import.meta.env;
const RAW_BASE = (isVite ? import.meta.env.VITE_API_BASE_URL : process.env.REACT_APP_API_BASE_URL) || "";
const KEY_ID = (isVite ? import.meta.env.VITE_API_KEY_IDENTITY : process.env.REACT_APP_API_KEY_IDENTITY) || "";
const KEY_CRED = (isVite ? import.meta.env.VITE_API_KEY_CREDENTIAL : process.env.REACT_APP_API_KEY_CREDENTIAL) || "";
const BASE_URL = RAW_BASE.trim().replace(/\/+$/, "");

const http = axios.create({ timeout: 30000 });

/** * 🔐 Helper: แปะ API Key เข้าท้าย URL
 * (จำเป็นสำหรับการโหลดไฟล์ PDF จาก Server)
 */
export const withKeys = (url) => {
  if (!url) return null;
  if (!KEY_ID || !KEY_CRED) return url;
  // ถ้ามี key อยู่แล้วไม่ต้องต่อเพิ่ม
  if (url.includes("key_identity=")) return url;
  
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}key_identity=${encodeURIComponent(KEY_ID)}&key_credential=${encodeURIComponent(KEY_CRED)}`;
};

/**
 * 🔗 Helper: สร้าง URL API
 */
export const api = (path) => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${BASE_URL}${clean}`;
};

/**
 * 📡 Main Fetcher (ใช้แทน axios ตรงๆ เพื่อดัก Error)
 */
export async function fetchJsonWithProxies(url) {
  try {
    const res = await http.get(withKeys(url));
    return res.data;
  } catch (e) {
    console.warn("Fetch Error:", url, e.message);
    return null;
  }
}

/** * 🔧 Helper: สร้าง Query String จาก Object 
 */
function buildQuery(obj = {}) {
  const pairs = [];
  const enc = (v) => encodeURIComponent(String(v));
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      v.forEach((val) => pairs.push(`${enc(k)}[]=${enc(val)}`));
    } else {
      pairs.push(`${enc(k)}=${enc(v)}`);
    }
  });
  return pairs.join("&");
}

// src/lib/omekaClient.js (ส่วนที่ต้องแก้ไข)

/* =========================================
   📚 LIST & SEARCH FUNCTIONS
   ========================================= */

export async function fetchItemsLite({
  limit = 12,
  page = 1,
  sortBy = "created",
  sortOrder = "desc",
  query = "",
  hasMedia = null,
  ...restFilters
} = {}) {
  
  // Map Sort Keys
  let apiSortBy = sortBy;
  if (sortBy === "year" || sortBy === "date") apiSortBy = "dcterms:date"; 
  else if (sortBy === "title" || sortBy === "name") apiSortBy = "dcterms:title";
  else if (sortBy === "created") apiSortBy = "created"; 

  const queryParams = {
    per_page: limit,
    page: page,
    sort_by: apiSortBy,
    sort_order: sortOrder,
  };

  if (hasMedia !== null) queryParams.has_media = hasMedia;
  
  // 🟢 แก้ไข: ใช้ Full-text search ของ Omeka S แทนการหาเฉพาะ Title แบบเป๊ะๆ
  if (query && query.trim()) {
    queryParams.search = query.trim();
  }

  const queryString = buildQuery({ ...queryParams, ...restFilters });
  const url = api(`/items?${queryString}`);
  
  const data = await fetchJsonWithProxies(url);
  return Array.isArray(data) ? data : [];
}

export async function searchOmeka(query) {
  if (!query || query.trim().length === 0) return [];
  const qTrim = query.trim();
  
  // 🟢 แก้ไข: ใช้ Full-text search พารามิเตอร์ `search=`
  const url = api(`/items?search=${encodeURIComponent(qTrim)}&per_page=12`);
  
  const data = await fetchJsonWithProxies(url);
  return Array.isArray(data) ? data : [];
}

/* =========================================
   🖼️ MEDIA & PDF LOGIC (ฉบับแก้ไขใหม่)
   ========================================= */

export const isExternalPdf = (url) => {
  if (!url) return false;
  return url.startsWith("http") && !url.includes(BASE_URL);
};

export async function fetchExternalPdfBuffer(url) {
    // ฟังก์ชันนี้เผื่อไว้สำหรับโหลด PDF ข้าม Domain (CORS) ถ้าจำเป็น
    // แต่เบื้องต้น return null ไปก่อนเพื่อให้ code ไม่ error
    return null;
}

export async function pdfUrlOf(item) {
  if (!item) return null;

  // 1. ลองหาจาก dcterms:source (ลิงก์ภายนอก)
  const source = item["dcterms:source"];
  if (Array.isArray(source)) {
    const uriVal = source.find(s => s.type === "uri" || s["@id"]);
    if (uriVal) {
        const url = uriVal["@id"] || uriVal["@value"];
        // ถ้าเป็นไฟล์ภายนอก ส่ง URL กลับเลย
        // ถ้าเป็นไฟล์ภายใน ให้แนบ Key
        return isExternalPdf(url) ? url : withKeys(url);
    }
  }

  // 2. ลองหาจาก Media ของ Item
  try {
    const mediaUrl = api(`/media?item_id=${item["o:id"]}`);
    const mediaList = await fetchJsonWithProxies(mediaUrl);
    
    if (Array.isArray(mediaList) && mediaList.length > 0) {
      // หาไฟล์ PDF
      const pdfMedia = mediaList.find(m => (m["o:media_type"] || "").includes("pdf"));
      if (pdfMedia) {
        const originalUrl = pdfMedia["o:original_url"] || pdfMedia["o:source"];
        // แปะ Key เสมอถ้ามาจาก Omeka S เราเอง
        return withKeys(originalUrl);
      }
    }
  } catch (e) {
    console.error("Error finding PDF media:", e);
  }
  return null;
}

/* =========================================
   🏷️ METADATA HELPERS (คืนชีพให้แล้วครับ)
   ========================================= */

// ดึงชื่อเรื่อง
export const titleOf = (it) => {
  if (!it) return "Untitled";
  return it["o:title"] || it["dcterms:title"]?.[0]?.["@value"] || "Untitled";
};

// ดึงคำอธิบาย
export const descOf = (it) => {
  if (!it) return "";
  const desc = it["dcterms:description"]?.[0]?.["@value"];
  if (desc) return desc;
  const subject = it["dcterms:subject"]?.[0]?.["@value"];
  return subject ? `Tag: ${subject}` : "";
};

// ดึงรูปปก
export async function thumbUrlOf(item) {
  if (!item) return null;
  // กรณีมี thumbnail_display_urls มาให้แล้ว (จาก API บาง version)
  const itThumb = item?.thumbnail_display_urls;
  if (itThumb?.medium) return itThumb.medium;
  if (itThumb?.large) return itThumb.large;
  if (itThumb?.square) return itThumb.square;

  // ถ้าไม่มี ให้ลองไป fetch media ตัวแรก
  try {
    const mediaUrl = api(`/media?item_id=${item["o:id"]}&per_page=1`);
    const mediaList = await fetchJsonWithProxies(mediaUrl);
    if (mediaList && mediaList.length > 0) {
        const m = mediaList[0];
        return m?.thumbnail_display_urls?.medium || m?.thumbnail_display_urls?.square || null;
    }
  } catch (e) {
      return null;
  }
  return null;
}